import React, {
    createContext,
    useLayoutEffect,
    useState,
    useCallback,
} from 'react';
import {
    IdHotelOwner as IdHotelOwnerFromLocalStorage,
    getInitialLang,
    IdSystemUser as IdSystemUserFromLocalStorage,
    lsLanguageKey,
    defaultLanguage,
    defaultTimezone,
    username as usernameFromLocalStorage,
    userProfileKeys,
    getInitialTheme,
    setLSTheme,
    getSystemTheme,
} from '../utils/userProfileContextHelpers';
import convertToOvariant from '../utils/convertToOvariant';
import api, { cachedApi, setAxiosLanguage } from '../services/api';
import endpoints from '../utils/commonVariables/endpoints';
import { parseJson } from '../utils/usefulFunctions';
import {
    savableEntities,
    viewModelEntities,
} from '../utils/commonVariables/entityKeys';
import { ThemeTypes } from '../types/theme/themeTypes';
import { useNotification } from './NotificationsContext';
import { FeedbackMessages } from '../utils/commonVariables/messages';

const contractSystemUser = {
    IdUserProfile: true,
    UserProfile: {
        ProfileData: true,
    },
    SystemRoles: {
        EntityId: true,
    },
};

export const ContextUserProfile = createContext();
// contexto para todo lo relacionado con el usuario. Login, settings, language...

export const ProviderUserProfile = props => {
    const { children } = props;
    const { setErrorMessage } = useNotification();
    ///////COSAS RELACIONADAS CON EL LOGING

    const [IdSystemUser, setIdSystemUser] = useState(
        IdSystemUserFromLocalStorage
    );
    const [IdHotelOwner, setIdHotelOwner] = useState(
        IdHotelOwnerFromLocalStorage
    );
    const [username, setUsername] = useState(usernameFromLocalStorage);

    const [settings, setSettings] = useState({
        idUserProfile: 0,
        language: getInitialLang(),
        timeZone: defaultTimezone,
        theme: getInitialTheme(),
        readOnlyUser: false,
        adminUser: false,
        // si es el usuario principal, sino es uno de la lista de usuarios
        mainUser: false,
    });
    const { language, timeZone, idUserProfile, readOnlyUser, theme } = settings;

    // llamada general para guardar settings en la base de datos
    const saveSettings = useCallback(
        async (settingsToSend, oldSettings) => {
            delete settingsToSend.idUserProfile;
            const newSystemUser = {
                UserProfile: {
                    ProfileData: JSON.stringify(settingsToSend),
                },
            };

            const contract = {
                UserProfile: { ProfileData: true },
            };

            const objToSend = convertToOvariant(
                savableEntities.SystemUser,
                newSystemUser,
                IdSystemUser,
                contract
            );
            try {
                let { status } = await api.post(
                    endpoints.commonSave,
                    objToSend
                );
                if (status !== 201) {
                    throw new Error();
                }
            } catch (error) {
                setSettings(oldSettings);
                console.log(error);
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            }
        },
        [IdSystemUser]
    );

    //cambias una clave y la guardas directamente en la base de datos
    const saveNewSettings = useCallback(
        async (newInfo, key) => {
            let oldSettings = {};
            setSettings(prevSettings => {
                oldSettings = { ...prevSettings };
                return { ...prevSettings, [key]: newInfo };
            });

            let newData = { ...oldSettings, [key]: newInfo };
            return saveSettings(newData, oldSettings);
        },
        [saveSettings]
    );

    const getInitialProfileData = async () => {
        const endpoint = endpoints.viewModel(
            viewModelEntities.SystemUser,
            IdSystemUser
        );

        try {
            const { data } = await api.post(endpoint, contractSystemUser);
            if (data?.Result) {
                return data?.Result;
            } else {
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        }
    };

    const handleProfileData = async (ownerSigned, userProfile) => {
        const profileData = parseJson(userProfile.UserProfile?.ProfileData);
        const isEmptyProfileData = !Object.keys(profileData).length;
        const doesNotHaveAllNeededProperties =
            profileData && userProfileKeys.some(key => !(key in profileData));

        const initialTheme = profileData?.theme || getInitialTheme();
        if (profileData?.theme && profileData?.theme !== getInitialTheme()) {
            setLSTheme(profileData?.theme);
        }

        if (isEmptyProfileData || doesNotHaveAllNeededProperties) {
            const defaultLanguageForSave = getInitialLang();
            const defaultTimezoneForSave =
                Intl.DateTimeFormat().resolvedOptions().timeZone;

            const newSystemUserData = {
                ...userProfile,
                UserProfile: {
                    ...userProfile.UserProfile,
                    ProfileData: JSON.stringify({
                        language:
                            profileData?.language || defaultLanguageForSave,
                        timeZone:
                            profileData?.timeZone || defaultTimezoneForSave,
                        theme: initialTheme,
                        ...profileData,
                    }),
                },
            };
            setSettings(prevSet => {
                return {
                    ...prevSet,
                    idUserProfile: userProfile.IdUserProfile,
                    language: defaultLanguage,
                    timeZone: defaultTimezone,
                    theme: initialTheme,
                };
            });
            const objToSend = convertToOvariant(
                savableEntities.SystemUser,
                newSystemUserData,
                IdSystemUser,
                contractSystemUser
            );
            let { data } = await api.post(endpoints.commonSave, objToSend);
            const newProfileData = parseJson(
                data.Result?.UserProfile?.ProfileData
            );
            setSettings(prev => {
                return {
                    ...prev,
                    ...newProfileData,
                };
            });
            setLSTheme(initialTheme);
        } else {
            setSettings(prevSet => {
                return {
                    ...prevSet,
                    idUserProfile: userProfile.IdUserProfile,
                    language: profileData?.language,
                    timeZone: profileData?.timeZone,
                    theme: profileData?.theme || getInitialTheme(),
                };
            });
        }
    };

    ///// COSAS RELACIONADAS CON LOS SETTINGS
    const setLanguage = lang => {
        setSettings(prevSet => {
            return { ...prevSet, language: lang };
        });
    };

    const setTimeZone = timeZoneKey => {
        setSettings(prevSet => {
            return { ...prevSet, timeZone: timeZoneKey };
        });
    };

    const setTheme = themeKey => {
        setSettings(prevSet => {
            return { ...prevSet, theme: themeKey };
        });
    };

    const updateSettings = newSettings => {
        setSettings(prevSet => {
            return { ...prevSet, ...newSettings };
        });
    };

    useLayoutEffect(() => {
        localStorage.setItem(lsLanguageKey, language);
        // api.ChangeAxiosLanguage(language);
        // cachedApi.ChangeAxiosLanguage(language);
        setAxiosLanguage(api, language);
        setAxiosLanguage(cachedApi, language);
    }, [language]);

    useLayoutEffect(() => {
        if (theme) {
            const newTheme =
                theme === ThemeTypes.SYSTEM ? getSystemTheme() : theme;
            document.documentElement.classList.remove(
                ThemeTypes.LIGHT,
                ThemeTypes.DARK
            );
            document.documentElement.classList.add(newTheme);
        }
    }, [theme]);

    return (
        <ContextUserProfile.Provider
            value={{
                IdHotelOwner,
                language,
                timeZone,
                setTimeZone,
                setSettings,
                updateSettings,
                saveNewSettings,
                idUserProfile,
                setLanguage,
                settings,
                saveSettings,
                IdSystemUser,
                setIdSystemUser,
                setIdHotelOwner,
                readOnlyUser,
                setUsername,
                username,
                theme,
                setTheme,
                getInitialProfileData,
                handleProfileData,
            }}
        >
            {children}
        </ContextUserProfile.Provider>
    );
};
